import { useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { GTooltip } from '../g-tooltip'
import { GRadioProps } from './types'
import { AiOutlineQuestionCircle } from 'react-icons/ai'

export function GRadio<T>(props: GRadioProps<T>) {
	const {
		label,
		description,
		value,
		name,
		checked,
		disabled,
		tooltipPosition = 'top',
		labelClass,
		className,
		inputContainerClassName,
		onChange,
		helpToolTip
	} = props

	const [isChecked, changeIsChecked] = useState(checked ? checked : false)

	useEffect(() => {
		changeIsChecked(checked)
	}, [checked])

	// The unchecked disc used to be `bg-gray-50`, which is `#121122` in dark.
	// That is the canvas, below every surface, so the empty radio read as a hole
	// on a popover or a card. `--radio-fill` is transparent in dark, which fits
	// every surface the radio sits on, and it holds the former `#f9fafb` in
	// light, so a lavender row hover cannot show through the disc there.
	//
	// The plugin `@tailwindcss/forms` declares `[type='radio']:checked:hover`
	// and `:checked:focus` with `background-color: currentColor`, and it sets
	// `color: #2563eb` in its own base. The old `text-button` class does not
	// exist in the config, so it painted nothing and the checked disc flipped to
	// that blue on hover and on focus. `text-t-dark-blue` fixes the source, and
	// the explicit `checked:hover` and `checked:focus` pairs below hold the fill
	// and the border even if the plugin base changes.
	const inputClassName = [
		'checked:border-t-dark-blue checked:bg-t-dark-blue',
		'checked:hover:border-t-dark-blue checked:hover:bg-t-dark-blue',
		'checked:focus:border-t-dark-blue checked:focus:bg-t-dark-blue'
	].join(' ')

	return (
		<div className={twMerge('flex items-center', className)}>
			<div className={twMerge('mr-3 -mt-1', inputContainerClassName)}>
				<input
					id={`radio-${name}-${value}`}
					name={name}
					type="radio"
					value={value}
					disabled={disabled}
					checked={isChecked}
					onChange={(e) => onChange(e)}
					className={twMerge(
						`
							h-4 w-4 border-2 cursor-pointer text-t-dark-blue
							bg-[color:var(--radio-fill)] border-[color:var(--radio-ring)]
							checked:border-4 disabled:opacity-50 disabled:cursor-not-allowed
							focus:ring-2 focus:ring-offset-0 focus:ring-t-dark-blue
						`,
						inputClassName
					)}
				/>
			</div>
			{/* The input carried `disabled:opacity-50`, but the label and the
			    description sat outside it, so a disabled row still read at full
			    strength and still showed a pointer cursor. */}
			<div className={disabled ? 'opacity-50' : undefined}>
				<label
					htmlFor={`radio-${name}-${value}`}
					className={twMerge(
						'text-md font-medium text-t-base-blue flex items-center',
						disabled ? 'cursor-not-allowed' : 'cursor-pointer',
						labelClass
					)}
				>
					{label ? label : ''}
					&nbsp;
					{helpToolTip && (
						<GTooltip
							arrowClassName="w-0 h-0 absolute bottom-[0.5px] border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-tooltip-caret"
							contentClassName="border-none font-grotesk mb-2 bg-t-smoke w-[300px] font-medium text-sm text-t-desc-gray"
							className="font-normal"
							content={helpToolTip}
							position={tooltipPosition}
						>
							<AiOutlineQuestionCircle className="w-4 h-4 text-t-gray-hold" />
						</GTooltip>
					)}
				</label>
				<div className="flex">
					{description && (
						<p className="mt-1 text-sm text-t-secondary">{description}</p>
					)}
				</div>
			</div>
		</div>
	)
}
